import React, { useState, useEffect } from "react";
import {
  BookOpen, Trophy, Cpu, Shield, Users, FileText, Rocket,
  Lightbulb, Laptop, Languages, Stethoscope, Landmark,
  Train, X, Sparkles, ChevronRight, Lock, CheckCircle, Loader2
} from "lucide-react";
import { useTheme } from "../ThemeContext";
import { submitContactForm } from "../../../services/contactApi"; // Importing your API service

const Coaching = () => {
  const { isDarkMode, theme } = useTheme();
  const activeMode = isDarkMode || theme === 'dark';

  // --- State Management ---
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState("CBSE");
  
  // Lead Gen State
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false); // Tracks if user has filled form in this session
  const [pendingAction, setPendingAction] = useState(null); // Stores the link/action user wanted to take
  
  // Form State
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    phone: "",
    city: "",
    class: "",
    subject: "General Inquiry"
  });

  // --- Helpers ---
  const getClassesForBoard = (board) => {
    return board === "ISC" ? [11, 12] : [6, 7, 8, 9, 10, 11, 12];
  };

  // Check session storage on mount to see if user already submitted previously
  useEffect(() => {
    const sessionUnlocked = sessionStorage.getItem("vidyazon_lead_unlocked");
    if (sessionUnlocked === "true") {
      setIsUnlocked(true);
    }
  }, []);

  // Handle Initial Click (The Gatekeeper)
  const handleItemClick = (item) => {
    // Determine the action to take after form submission
    const action = () => {
      if (item.name === "Board Curriculum") {
        setShowBoardModal(true);
      } else {
        window.open(`https://aespleduzone.com${item.link}`, '_blank');
      }
    };

    // If already unlocked, proceed immediately
    if (isUnlocked) {
      action();
    } else {
      // Otherwise, store the action and open the Lead Form
      setPendingAction(() => action);
      setFormData(prev => ({ ...prev, subject: `Inquiry: ${item.name}` }));
      setShowLeadModal(true);
    }
  };

  // Handle Board specific class click (also gated implicitly by the parent modal)
  const handleBoardClassClick = (classNum) => {
    const boardPath = selectedBoard.toLowerCase();
    window.open(`https://aespleduzone.com/board/${boardPath}?curriculum=Class-${classNum}`, '_blank');
    setShowBoardModal(false);
  };

  // Form Handlers
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // API Call
      await submitContactForm({
        firstName: formData.firstName,
        lastName: "", // Optional in UI, handled as empty
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        class: formData.class,
        subject: formData.subject,
        message: "Unlock Access Request via Coaching Page"
      });

      // Success Sequence
      setSubmitSuccess(true);
      sessionStorage.setItem("vidyazon_lead_unlocked", "true");
      setIsUnlocked(true);

      // Wait a moment for the user to see success, then execute original action
      setTimeout(() => {
        setShowLeadModal(false);
        setSubmitSuccess(false);
        setLoading(false);
        if (pendingAction) pendingAction();
      }, 2000);

    } catch (error) {
      console.error("Submission failed", error);
      alert("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  // --- Data ---
  const sections = [
    {
      id: "school",
      title: "School Curriculum",
      description: "Foundation building and board preparations for future leaders.",
      gradient: "from-blue-500 to-cyan-500",
      lightBg: "bg-blue-500/10",
      icon: <BookOpen />,
      items: [
        { name: "Board Curriculum", icon: <BookOpen />, link: "#" },
        { name: "Skilling Courses", icon: <Cpu />, link: "/school-courses/skilling-courses-school" },
        { name: "Foundation Courses", icon: <Shield />, link: "/school-courses/aptitude60" },
        { name: "Career Counselling", icon: <Users />, link: "/school-courses/career-counseling-school" },
        { name: "Project Work", icon: <FileText />, link: "/project-work/school-level" },
        { name: "Project Tour", icon: <Rocket />, link: "/industrial-tour" },
        { name: "Entrepreneureship", icon: <Lightbulb />, link: "/course/entrepreneurship-development-program" },
        { name: "Computer Courses", icon: <Laptop />, link: "/school-courses/computer-course-school" },
        { name: "Language Courses", icon: <Languages />, link: "/school-courses/orientation-courses" },
      ],
    },
    {
      id: "competitive",
      title: "Competitive Prep",
      description: "Ace your entrance exams with expert strategic guidance.",
      gradient: "from-purple-600 to-pink-600",
      lightBg: "bg-purple-500/10",
      icon: <Trophy />,
      items: [
        { name: "Engineering", icon: <Cpu />, link: "/competitive-curriculum/engineering" },
        { name: "Medical", icon: <Stethoscope />, link: "/competitive-curriculum/medical" },
        { name: "Bank Exams", icon: <Landmark />, link: "/competitive-curriculum/competitive-bank-exams" },
        { name: "Foundation", icon: <BookOpen />, link: "/competitive-curriculum/foundation" },
        { name: "SSC", icon: <FileText />, link: "/competitive-curriculum/staff-selection-commission-ssc" },
        { name: "Defence", icon: <Shield />, link: "/competitive-curriculum/defence" },
        { name: "UPSSSC", icon: <FileText />, link: "/competitive-curriculum/upsssc" },
        { name: "Railways", icon: <Train />, link: "/competitive-curriculum/railways" },
        { name: "State Level Exams", icon: <Landmark />, link: "/competitive-curriculum/state-level-exams" },
      ],
    },
  ];

  return (
    <div className={`min-h-screen relative transition-colors duration-500 font-sans ${activeMode ? 'bg-[#050505] text-white' : 'bg-slate-50 text-slate-900'}`}>
      
      {/* Dynamic Background Decor */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full blur-[120px] opacity-20 bg-blue-600 animate-pulse`} />
        <div className={`absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full blur-[120px] opacity-20 bg-purple-600 animate-pulse`} style={{animationDelay: '1s'}} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2v-4h4v-2h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2v-4h4v-2H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
      </div>

      <div className="relative z-10 pt-32 pb-20 px-4 max-w-7xl mx-auto space-y-24">
        
        {/* Animated Header */}
        <div className="text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-500 text-sm font-bold uppercase tracking-widest animate-fade-in-up">
            <Sparkles className="w-4 h-4" /> AESPL eduZone Prep
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-4 tracking-tight leading-tight">
            Master Your <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500">
              Future Success
            </span>
          </h1>
          <p className={`text-lg md:text-xl max-w-2xl mx-auto font-medium ${activeMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Unlock premium educational resources. Access requires a quick verification step to ensure personalized guidance.
          </p>
        </div>

        {/* Content Sections */}
        {sections.map((section) => (
          <div key={section.id} className="space-y-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-gray-500/10 pb-6">
              <div className="flex items-center gap-5">
                <div className={`p-4 rounded-2xl bg-gradient-to-br ${section.gradient} text-white shadow-xl shadow-blue-500/20 transform -rotate-3`}>
                  {React.cloneElement(section.icon, { className: "w-8 h-8" })}
                </div>
                <div>
                  <h2 className={`text-3xl md:text-4xl font-black ${activeMode ? 'text-white' : 'text-gray-900'}`}>{section.title}</h2>
                  <p className={`text-base font-medium mt-1 ${activeMode ? 'text-gray-400' : 'text-gray-500'}`}>{section.description}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {section.items.map((item, idx) => (
                <div key={idx} onClick={() => handleItemClick(item)} className="group relative">
                  {/* Glowing Background Effect */}
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${section.gradient} rounded-2xl opacity-0 group-hover:opacity-100 blur-md transition duration-500`} />
                  
                  <div className={`relative h-full rounded-2xl p-8 border transition-all duration-300 flex flex-col items-center text-center gap-5 cursor-pointer overflow-hidden ${activeMode ? 'bg-[#0f0f0f] border-white/5 group-hover:bg-black' : 'bg-white border-slate-200 shadow-sm group-hover:shadow-2xl'}`}>
                    
                    {/* Lock Icon indicator if not unlocked */}
                    {!isUnlocked && (
                       <div className="absolute top-4 right-4 text-gray-400/50 group-hover:text-purple-500 transition-colors">
                          <Lock className="w-4 h-4" />
                       </div>
                    )}

                    {/* Floating Icon Design */}
                    <div className={`p-4 rounded-2xl transition-all duration-500 group-hover:scale-110 group-hover:-rotate-6 ${activeMode ? 'bg-white/5 text-blue-400 group-hover:bg-blue-500 group-hover:text-white' : 'bg-slate-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white'}`}>
                      {React.cloneElement(item.icon, { className: "w-10 h-10" })}
                    </div>

                    <h3 className={`font-bold text-lg leading-tight transition-colors ${activeMode ? 'text-gray-200 group-hover:text-white' : 'text-slate-800'}`}>
                      {item.name}
                    </h3>

                    <div className={`mt-auto inline-flex items-center gap-2 text-sm font-extrabold uppercase tracking-tighter opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 ${activeMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      {isUnlocked ? "Access Now" : "Unlock"} <ChevronRight className="w-4 h-4" />
                    </div>
                    
                    {/* Decorative Number */}
                    <div className="absolute -bottom-4 -right-4 text-6xl font-black opacity-[0.02] group-hover:opacity-[0.05] transition-opacity">
                      {idx + 1}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* --- LEAD GENERATION MODAL (MANDATORY GATE) --- */}
      {showLeadModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md animate-in fade-in duration-300" onClick={() => !loading && setShowLeadModal(false)} />
          
          <div className={`relative w-full max-w-lg rounded-3xl shadow-2xl p-8 md:p-10 animate-in zoom-in-95 duration-300 overflow-hidden ${activeMode ? 'bg-gray-900 border border-white/10' : 'bg-white'}`}>
             
             {/* Modal Decor */}
             <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/20 rounded-bl-full blur-2xl" />
             <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/20 rounded-tr-full blur-2xl" />

             {!loading && !submitSuccess && (
                <button onClick={() => setShowLeadModal(false)} className="absolute top-5 right-5 p-2 rounded-full hover:bg-gray-500/10 transition z-20">
                   <X className={`w-5 h-5 ${activeMode ? 'text-gray-400' : 'text-gray-600'}`} />
                </button>
             )}

             {submitSuccess ? (
                // Success View
                <div className="flex flex-col items-center justify-center text-center py-10 space-y-4">
                   <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-2">
                      <CheckCircle className="w-10 h-10 text-green-500" />
                   </div>
                   <h3 className={`text-2xl font-black ${activeMode ? 'text-white' : 'text-gray-900'}`}>Access Unlocked!</h3>
                   <p className="text-gray-500">Redirecting you to your resource...</p>
                </div>
             ) : (
                // Form View
                <div className="relative z-10">
                   <div className="mb-6">
                      <h2 className={`text-2xl md:text-3xl font-black mb-2 ${activeMode ? 'text-white' : 'text-gray-900'}`}>
                         Unlock Full Access
                      </h2>
                      <p className="text-sm font-medium text-gray-500">
                         Complete your profile to view curriculum details and access course materials.
                      </p>
                   </div>

                   <form onSubmit={onFormSubmit} className="space-y-4">
                      <div className="space-y-1">
                         <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Full Name</label>
                         <input 
                            required 
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            type="text" 
                            className={`w-full p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all ${activeMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-gray-50 text-gray-900 border border-gray-200'}`} 
                            placeholder="John Doe"
                         />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                         <div className="space-y-1">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Phone</label>
                            <input 
                               required 
                               name="phone"
                               value={formData.phone}
                               onChange={handleInputChange}
                               type="tel" 
                               className={`w-full p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all ${activeMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-gray-50 text-gray-900 border border-gray-200'}`} 
                               placeholder="+91 987..."
                            />
                         </div>
                         <div className="space-y-1">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">City</label>
                            <input 
                               required 
                               name="city"
                               value={formData.city}
                               onChange={handleInputChange}
                               type="text" 
                               className={`w-full p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all ${activeMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-gray-50 text-gray-900 border border-gray-200'}`} 
                               placeholder="Mumbai..."
                            />
                         </div>
                      </div>

                      <div className="space-y-1">
                         <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Email</label>
                         <input 
                            required 
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            type="email" 
                            className={`w-full p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all ${activeMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-gray-50 text-gray-900 border border-gray-200'}`} 
                            placeholder="john@example.com"
                         />
                      </div>

                      <div className="space-y-1">
                         <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Class/Grade</label>
                         <select 
                            required
                            name="class"
                            value={formData.class}
                            onChange={handleInputChange}
                            className={`w-full p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none ${activeMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-gray-50 text-gray-900 border border-gray-200'}`} 
                         >
                            <option value="">Select Class</option>
                            {[6,7,8,9,10,11,12].map(c => <option key={c} value={`Class ${c}`}>Class {c}</option>)}
                            <option value="Dropper">12+ (Dropper)</option>
                         </select>
                      </div>

                      <button 
                         type="submit" 
                         disabled={loading}
                         className={`w-full py-4 mt-4 rounded-xl font-black text-sm uppercase tracking-widest text-white shadow-lg transition-all transform hover:-translate-y-1 ${loading ? 'bg-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-purple-600 shadow-blue-500/30 hover:shadow-blue-500/50'}`}
                      >
                         {loading ? (
                            <span className="flex items-center justify-center gap-2">
                               <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                            </span>
                         ) : (
                            "Unlock Access"
                         )}
                      </button>
                   </form>
                </div>
             )}
          </div>
        </div>
      )}

      {/* --- Board Selection Modal (Existing) --- */}
      {showBoardModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setShowBoardModal(false)} />
          <div className={`relative w-full max-w-2xl rounded-[2.5rem] shadow-2xl p-6 md:p-12 text-center animate-in zoom-in slide-in-from-bottom-10 duration-500 overflow-hidden ${activeMode ? 'bg-gray-900 border border-white/10' : 'bg-white'}`}>
            
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-blue-600/20 blur-[80px]" />
            
            <button onClick={() => setShowBoardModal(false)} className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-500/10 transition">
              <X className={`w-6 h-6 ${activeMode ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>

            <div className="relative">
              <h2 className={`text-3xl md:text-4xl font-black mb-2 ${activeMode ? 'text-white' : 'text-blue-900'}`}>Select Your Board</h2>
              <p className="text-gray-500 mb-8 font-medium">Choose your academic board to view specific curriculum</p>
              
              <div className="flex flex-wrap justify-center gap-3 mb-10 bg-gray-500/5 p-2 rounded-2xl">
                {['CBSE', 'ISC', 'ICSE'].map((board) => (
                  <button
                    key={board}
                    onClick={() => setSelectedBoard(board)}
                    className={`flex-1 min-w-[100px] px-6 py-3 rounded-xl text-sm font-black transition-all duration-300 ${selectedBoard === board ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30 scale-105' : 'text-gray-500 hover:bg-gray-500/10'}`}
                  >
                    {board}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {getClassesForBoard(selectedBoard).map((classNum) => (
                  <div key={classNum} onClick={() => handleBoardClassClick(classNum)} className={`group cursor-pointer rounded-2xl border transition-all duration-300 hover:-translate-y-2 hover:shadow-xl ${activeMode ? 'border-white/5 bg-white/5 hover:bg-white/10' : 'border-gray-100 bg-gray-50 hover:bg-white'}`}>
                    <div className="h-20 flex items-center justify-center">
                      <span className={`text-4xl font-black transition-colors ${activeMode ? 'text-blue-400' : 'text-blue-900'}`}>{classNum}</span>
                    </div>
                    <div className="bg-gradient-to-r from-orange-500 to-pink-500 py-2.5 text-white text-[10px] font-black uppercase tracking-[0.2em]">Class</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coaching;